// The shared presentational primitives (doc 26 §4), used by every audience:
// the med-spa portal, the telehealth surface, sign-in and the attorney
// console. No data logic, and deliberately NO motion library — this module is
// in the shared bundle; the animated pieces (Segmented, Drawer, Toast host)
// live in the customer chunk.
//
// Every control renders through one of these so the six microstates —
// default, hover, focus, active, disabled, busy — are designed once. A busy
// button keeps its width and swaps its label for a spinner, so a click on a
// control that takes a moment is visibly acknowledged and the layout never
// jumps (a second click on a write is a second record).

import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { usePageHeaderExtra } from './PageHeaderExtras';
import { Info } from 'lucide-react';
import { useOverflows } from '../hooks/useOverflows';
import { initials } from './initials';
export { initials } from './initials';

// ---- Buttons ---------------------------------------------------------------
type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  busy?: boolean;
  icon?: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', busy = false, icon, className, children, disabled, ...rest },
  ref,
) {
  const cls = [
    'vx-btn',
    `vx-btn--${variant}`,
    size === 'sm' ? 'vx-btn--sm' : '',
    busy ? 'vx-btn--busy' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button
      ref={ref}
      type="button"
      className={cls}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

// ---- Fields ----------------------------------------------------------------
export function Field({
  label,
  hint,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={`vx-field${className ? ` ${className}` : ''}`}>
      <span>{label}</span>
      {children}
      {hint && <small className="vx-field__hint">{hint}</small>}
    </label>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function TextInput({ className, ...rest }, ref) {
    return <input ref={ref} className={`vx-input${className ? ` ${className}` : ''}`} {...rest} />;
  },
);

export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <span className={`vx-select${className ? ` ${className}` : ''}`}>
      <select className="vx-input" {...rest}>
        {children}
      </select>
    </span>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`vx-textarea${className ? ` ${className}` : ''}`} {...rest} />;
}

/** A whole labelled box is the target — the checkbox is inside it. */
export function CheckCard({
  checked,
  onChange,
  children,
  name,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
  name?: string;
}) {
  return (
    <label className="vx-check-card" data-checked={checked ? 'true' : 'false'}>
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{children}</span>
    </label>
  );
}

// ---- Layout pieces ---------------------------------------------------------
export function Card({ children, variant = 'default', className }: {
  children: ReactNode;
  variant?: 'default' | 'quiet' | 'hero';
  className?: string;
}) {
  return <div className={`vx-card${variant !== 'default' ? ` vx-card--${variant}` : ''}${className ? ` ${className}` : ''}`}>{children}</div>;
}

export function SectionHeader({
  eyebrow,
  title,
  lead,
  statement = false,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  /** Keep an existing title and lead as one typographic statement. */
  statement?: boolean;
}) {
  const titleId = useId();
  const leadId = useId();
  const afterHeader = usePageHeaderExtra();
  return (
    <>
      <header className={`vx-section-head${statement ? ' vx-section-head--statement' : ''}`}>
        {eyebrow && <p className="vx-eyebrow">{eyebrow}</p>}
        {statement && lead ? <h1 aria-labelledby={titleId} aria-describedby={leadId}><span id={titleId}>{title}</span> <span id={leadId} className="vx-statement__lead">{lead}</span></h1> : <><h1>{title}</h1>{lead && <p className="vx-lead">{lead}</p>}</>}
      </header>
      {afterHeader}
    </>
  );
}

export function Block({
  title,
  titleVisuallyHidden = false,
  hint,
  aside,
  children,
  className,
  variant = 'default',
}: {
  title?: ReactNode;
  /** Render `title` as a heading a screen reader still announces, but with
   *  nothing on screen — for a caller whose visual design has the title
   *  shown elsewhere (a tab, a segmented control) and would otherwise skip
   *  a heading level by omitting it outright (BL-608). */
  titleVisuallyHidden?: boolean;
  hint?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  variant?: 'default' | 'quiet' | 'hero';
}) {
  return (
    <section className={`vx-block${variant !== 'default' ? ` vx-block--${variant}` : ''}${className ? ` ${className}` : ''}`}>
      {(title || aside) && (
        <header className="vx-block__head">
          <div>
            {title && (
              <h2 className={`vx-block__title${titleVisuallyHidden ? ' vx-sr-only' : ''}`}>{title}</h2>
            )}
            {hint && <p className="vx-block__hint">{hint}</p>}
          </div>
          {aside}
        </header>
      )}
      {children}
    </section>
  );
}

export function ActionBar({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  // The bar is sticky at the bottom edge, so it sits on top of whatever the
  // viewport is showing there — including a field the browser has just
  // scrolled into view because it took focus. At 390px, tabbing to "Full
  // name" on the People step landed it at exactly that scroll position: the
  // field had focus and what you saw was "Continue to Locations".
  // scroll-padding-block-end (portal.css) is what holds a scroll-into-view
  // clear of it, and it needs the bar's height — which no stylesheet can
  // know, because it depends on the note's wording: 99px on People and 122px
  // on Locations at the same width, and different again the next time a note
  // is reworded. So the bar measures itself and publishes the number, and
  // nobody has to remember to update a constant.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const publish = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      root.style.setProperty('--vx-actionbar-h', h + 'px');
    };
    publish();
    const watch = new ResizeObserver(publish);
    watch.observe(el);
    return () => {
      watch.disconnect();
      // A step with no bar must not inherit the last step's padding. React
      // runs this cleanup before the next bar's effect, so an incoming bar
      // still gets to publish its own height.
      root.style.removeProperty('--vx-actionbar-h');
    };
  }, []);

  // Some browsers do not re-run their focus scrolling after the sticky bar
  // grows (for example when its note wraps at 390px). If a newly focused
  // control is behind this bar, move that control just above it. The measured
  // bar height and the global scroll-padding above keep the final position
  // clear on keyboard and programmatic focus paths alike.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let frame = 0;
    const onFocus = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || el.contains(target)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const bar = el.getBoundingClientRect();
        const rect = target.getBoundingClientRect();
        const sticky = getComputedStyle(el).position === 'sticky';
        if (sticky && rect.bottom > bar.top && rect.top < bar.bottom) {
          target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });
    };
    document.addEventListener('focusin', onFocus, true);
    return () => {
      document.removeEventListener('focusin', onFocus, true);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div className="vx-actionbar" ref={ref}>
      {children}
    </div>
  );
}

export function Tag({
  children,
  variant,
  title,
}: {
  children: ReactNode;
  variant?: 'faint' | 'ink' | 'mono';
  title?: string;
}) {
  return (
    <span className={`vx-tag${variant ? ` vx-tag--${variant}` : ''}`} title={title}>
      {children}
    </span>
  );
}

export function Callout({
  children,
  strong,
  icon,
}: {
  children: ReactNode;
  strong?: boolean;
  icon?: ReactNode;
}) {
  return (
    <div className={`vx-callout${strong ? ' vx-callout--strong' : ''}`} role="note">
      {icon ?? <Info size={16} aria-hidden="true" />}
      <div>{children}</div>
    </div>
  );
}

/** A count and its label, with no verdict implied by the tile itself. */
export function StatTile({ value, label, className }: { value: ReactNode; label: ReactNode; className?: string }) {
  return <div className={`vx-stat-tile${className ? ` ${className}` : ''}`}>
    <strong className="vx-stat-tile__value">{value}</strong>
    <span className="vx-stat-tile__label">{label}</span>
  </div>;
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="vx-empty">
      {icon}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ height = 16, width }: { height?: number; width?: string | number }) {
  return <div className="vx-skel" style={{ height, width }} aria-hidden="true" />;
}

/** A stack of skeleton rows standing in for a list that is loading. */
export function SkeletonRows({ rows = 3, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div className="vx-stack vx-stack--tight" role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={44} />
      ))}
    </div>
  );
}

export function Avatar({ name, icon }: { name?: string; icon?: ReactNode }) {
  return (
    <span className="vx-avatar" aria-hidden="true">
      {icon ?? (name ? initials(name) : '·')}
    </span>
  );
}

/** Inline error line under a form, announced to assistive technology. */
export function InlineError({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="vx-small" role="alert" style={{ color: 'var(--vx-no-ink)', marginTop: 'var(--vx-4)' }}>
      {children}
    </p>
  );
}

/** Drop-in for a scrolling wrapper (`.vx-scroll-x`, `.cx-diff-sample`, a wide
 *  table's own overflow box): a keyboard-only user can reach an overflowing
 *  region only if IT (not just something inside it) is a tab stop (WCAG
 *  2.1.1 / 2.1.3, axe `scrollable-region-focusable`). Picks up `tabIndex`,
 *  `role="region"` and `aria-label` only while it actually overflows, so a
 *  box that never scrolls never gets a meaningless stop. For a region that
 *  already carries its own role/name (a segmented control's `role="group"`),
 *  use `useOverflows` directly instead of this wrapper. */
export function ScrollRegion({
  className,
  style,
  label,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  label: string;
  children: ReactNode;
}) {
  const { ref, overflows } = useOverflows<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={className}
      style={style}
      {...(overflows ? { tabIndex: 0, role: 'region' as const, 'aria-label': label } : {})}
    >
      {children}
    </div>
  );
}
